import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { clearAdminBasicAuth, deleteJson, getJson, hasAdminBasicAuth, patchJson, postJson, saveAdminBasicAuth } from "../api";
import { FeedbackPanel, Input, PortalLayout, ProcessingPanel, StatusBadge, formatDate, formatDateTime } from "./shared";

const EMPTY_FORM = {};
const PAGE_SIZE = 12;
const ENTITY_TYPE_OPTIONS = [
  { value: "driver", label: "Chofer" },
  { value: "transport_company", label: "Empresa de transporte" },
  { value: "tractor_unit", label: "Unidad tractora" },
  { value: "trailer_unit", label: "Unidad remolque" },
];

const MODULES = [
  { key: "dashboard", label: "Tablero", icon: "Tablero" },
  { key: "users", label: "Usuarios", icon: "Usuarios", registry: "consumer_users" },
  { key: "transport_users", label: "Usuarios transporte", icon: "Usuarios transporte", registry: "transport_users" },
  { key: "transport_companies", label: "Empresas de transporte", icon: "Transporte", registry: "titulares" },
  { key: "consumer_companies", label: "Empresas consumidoras", icon: "Consumidoras", registry: "consumer_companies" },
  { key: "drivers", label: "Choferes", icon: "Choferes", registry: "choferes" },
  { key: "vehicles", label: "Vehículos", icon: "Vehículos", registry: "unidades" },
  { key: "trailers", label: "Remolques", icon: "Remolques", registry: "acoplados" },
  { key: "documents", label: "Documentos", icon: "Documentos", registry: "documentos" },
  { key: "operations", label: "Operaciones", icon: "Operaciones", registry: "operations" },
  { key: "authorization_requests", label: "Solicitudes de habilitación", icon: "Solicitudes", registry: "consumer_entity_authorizations" },
  { key: "document_access_requests", label: "Solicitudes de acceso documental", icon: "Accesos", registry: "document_access_grants" },
  { key: "document_policies", label: "Políticas documentales", icon: "Políticas" },
  { key: "transactions", label: "Transactions", icon: "Feed" },
  { key: "audit_log", label: "Audit Log", icon: "Auditoría" },
  { key: "settings", label: "Ajustes", icon: "Ajustes" },
];

const ENTITY_SECTION_ORDER = ["driver", "transport_company", "tractor_unit", "trailer_unit"];
const ENTITY_SECTION_LABELS = {
  driver: "Chofer",
  transport_company: "Empresa de transporte",
  tractor_unit: "Unidad tractora",
  trailer_unit: "Unidad remolque",
};
const PINNED_DOCUMENT_PRIORITIES = {
  driver: {
    dni_frente: 0,
    dni_dorso: 1,
    selfie_biometria: 2,
  },
  transport_company: {
    constancia_afip: 0,
    afip_certificate: 0,
  },
};

const EMPTY_ADMIN_LOGIN = { username: "", password: "" };

export function AdminDashboardScreen() {
  const [authenticated, setAuthenticated] = useState(() => hasAdminBasicAuth());
  const [form, setForm] = useState(EMPTY_ADMIN_LOGIN);
  const [submitting, setSubmitting] = useState(false);
  const [authError, setAuthError] = useState("");

  async function handleLogin(event) {
    event.preventDefault();
    setSubmitting(true);
    setAuthError("");

    try {
      saveAdminBasicAuth(form.username, form.password);
      await getJson("/admin");
      setAuthenticated(true);
      setForm(EMPTY_ADMIN_LOGIN);
    } catch (error) {
      clearAdminBasicAuth();
      setAuthError(error.message || "No pudimos validar las credenciales.");
    } finally {
      setSubmitting(false);
    }
  }

  function handleLogout() {
    clearAdminBasicAuth();
    setAuthenticated(false);
    setAuthError("");
  }

  if (!authenticated) {
    return (
      <PortalLayout
        eyebrow="Camionero Admin"
        title="Acceso restringido"
        description="El backoffice ya no aparece en la landing. Entrá por URL y validá usuario y contraseña."
        backHref="#/"
      >
        <section className="detail-panel">
          <div className="detail-panel-header">
            <h2>Login de admin</h2>
          </div>
          <form className="admin-form" onSubmit={handleLogin}>
            <div className="admin-form-grid">
              <Input label="Usuario" value={form.username} autoComplete="username" onChange={(value) => setForm((current) => ({ ...current, username: value }))} />
              <Input label="Contraseña" type="password" value={form.password} autoComplete="current-password" onChange={(value) => setForm((current) => ({ ...current, password: value }))} />
            </div>
            {authError ? <FeedbackPanel message={authError} error /> : null}
            <div className="admin-form-actions">
              <button className="submit-button" type="submit" disabled={submitting}>
                {submitting ? "Validando..." : "Ingresar"}
              </button>
            </div>
          </form>
        </section>
      </PortalLayout>
    );
  }

  return <AdminDashboardInner onLogout={handleLogout} />;
}

function AdminDashboardInner({ onLogout }) {
  const pendingNavigationRef = useRef(null);
  const [catalog, setCatalog] = useState([]);
  const [registryMap, setRegistryMap] = useState({});
  const [metrics, setMetrics] = useState(null);
  const [activity, setActivity] = useState([]);
  const [history, setHistory] = useState([]);
  const [activeModule, setActiveModule] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [globalSearch, setGlobalSearch] = useState("");
  const [moduleSearch, setModuleSearch] = useState("");
  const [page, setPage] = useState(1);
  const [moduleFilters, setModuleFilters] = useState({
    status: "",
    company: "",
    documentType: "",
    entityType: "",
    validationStatus: "",
  });
  const [editingRecordId, setEditingRecordId] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [formState, setFormState] = useState(EMPTY_FORM);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState("summary");
  const [detailOpen, setDetailOpen] = useState(false);
  const [transactionFilters, setTransactionFilters] = useState({
    entityType: "",
    status: "",
    dateFrom: "",
    dateTo: "",
  });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [requirementsLoading, setRequirementsLoading] = useState(false);
  const [requirementsError, setRequirementsError] = useState("");
  const [consumerCompanies, setConsumerCompanies] = useState([]);
  const [consumerCompaniesLoading, setConsumerCompaniesLoading] = useState(false);
  const [consumerCompaniesError, setConsumerCompaniesError] = useState("");
  const [documentLibrary, setDocumentLibrary] = useState([]);
  const [documentLibraryLoading, setDocumentLibraryLoading] = useState(false);
  const [documentLibraryError, setDocumentLibraryError] = useState("");
  const [selectedEntityForCatalog, setSelectedEntityForCatalog] = useState("driver");
  const ENTITY_SECTION_ORDER = ["driver", "transport_company", "tractor_unit", "trailer_unit"];
  const ENTITY_SECTION_LABELS = {
    driver: "Chofer",
    transport_company: "Empresa de transporte",
    tractor_unit: "Unidad tractora",
    trailer_unit: "Unidad remolque",
  };
  const fetchDocuments = useCallback(async () => {
    const response = await getJson("/document-policies/requirements");
    return response.data || [];
  }, []);
  const refreshDocuments = useCallback(async () => {
    setRequirementsLoading(true);
    setRequirementsError("");
    try {
      const next = await fetchDocuments();
      setRequirements(next);
    } catch (refreshError) {
      setRequirementsError(refreshError.message);
    } finally {
      setRequirementsLoading(false);
    }
  }, [fetchDocuments]);
  const fetchConsumerCompanies = useCallback(async () => {
    const response = await getJson("/admin/registries/consumer_companies?per_page=200");
    return response.data || [];
  }, []);
  const refreshConsumerCompanies = useCallback(async () => {
    setConsumerCompaniesLoading(true);
    setConsumerCompaniesError("");
    try {
      const next = await fetchConsumerCompanies();
      setConsumerCompanies(next);
    } catch (refreshError) {
      setConsumerCompaniesError(refreshError.message);
    } finally {
      setConsumerCompaniesLoading(false);
    }
  }, [fetchConsumerCompanies]);
  const fetchDocumentLibrary = useCallback(async () => {
    const response = await getJson("/document-policies/library");
    return response.data || [];
  }, []);
  const refreshDocumentLibrary = useCallback(async () => {
    setDocumentLibraryLoading(true);
    setDocumentLibraryError("");
    try {
      const next = await fetchDocumentLibrary();
      setDocumentLibrary(next);
    } catch (refreshError) {
      setDocumentLibraryError(refreshError.message);
    } finally {
      setDocumentLibraryLoading(false);
    }
  }, [fetchDocumentLibrary]);
  const groupedDocumentLibrary = useMemo(() => {
    const sections = {};
    (documentLibrary || []).forEach((entry) => {
      const entity = entry.entity_type;
      if (!entity) {
        return;
      }
      sections[entity] = sections[entity] || [];
      sections[entity].push(entry);
    });
    return sections;
  }, [documentLibrary]);

  const activeRegistryType = MODULES.find((module) => module.key === activeModule)?.registry || "";
  const activeRegistry = activeRegistryType ? registryMap[activeRegistryType] : null;
  const visibleRegistryFields = activeRegistry
    ? activeRegistryType === "documentos"
      ? activeRegistry.fields
      : activeRegistry.fields.slice(0, 5)
    : [];
  const detailPanelVisible = detailOpen || formOpen || Boolean(editingRecordId);

  useEffect(() => {
    let active = true;

    async function load() {
      setLoading(true);
      setError("");
      try {
        const dashboardResponse = await getJson("/admin");
        const registries = dashboardResponse.data.registries || [];

        if (!active) {
          return;
        }

        setCatalog(registries);
        setMetrics(dashboardResponse.data.metrics || null);
        setActivity(dashboardResponse.data.recentActivity || []);
        setHistory(dashboardResponse.data.history || []);
      } catch (loadError) {
        if (active) {
          setError(loadError.message);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(async () => {
      try {
        const [metricsResponse, activityResponse, historyResponse] = await Promise.all([
          getJson("/admin/metrics"),
          getJson("/admin/activity/recent"),
          getJson("/admin/history"),
        ]);
        setMetrics(metricsResponse.data || null);
        setActivity(activityResponse.data || []);
        setHistory(historyResponse.data || []);
      } catch (_pollError) {
        // silent polling for admin monitor
      }
    }, 7000);

    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const preserveNavigationState = pendingNavigationRef.current?.registryType === activeRegistryType;
    setPage(1);
    setModuleFilters({ status: "", company: "", documentType: "", entityType: "", validationStatus: "" });
    setEditingRecordId("");
    setFormOpen(false);
    setFormState(EMPTY_FORM);
    if (!preserveNavigationState) {
      setModuleSearch("");
      setDetail(null);
      setDetailOpen(false);
      setDetailTab("summary");
    }
    pendingNavigationRef.current = null;
  }, [activeModule, activeRegistryType]);

  useEffect(() => {
    if (activeModule !== "document_policies") {
      return;
    }

    refreshDocuments();
    refreshDocumentLibrary();
    refreshConsumerCompanies();

    return undefined;
  }, [activeModule, refreshConsumerCompanies, refreshDocuments, refreshDocumentLibrary]);

  useEffect(() => {
    if (!activeRegistry || editingRecordId || Object.keys(formState).length) {
      return;
    }

    setFormState(buildInitialForm(activeRegistry));
  }, [activeRegistry, editingRecordId, formState]);

  useEffect(() => {
    let active = true;

    async function loadRegistry() {
      if (!activeRegistryType) {
        return;
      }
      try {
        const params = new URLSearchParams({
          page: String(page),
          pageSize: String(PAGE_SIZE),
        });
        if (globalSearch.trim()) {
          params.set("search", globalSearch.trim());
        }
        if (moduleSearch.trim()) {
          params.set("search", moduleSearch.trim());
        }
        if (moduleFilters.status.trim()) {
          params.set("status", moduleFilters.status.trim());
        }
        if (moduleFilters.company.trim()) {
          params.set("company", moduleFilters.company.trim());
        }
        if (moduleFilters.documentType.trim()) {
          params.set("documentType", moduleFilters.documentType.trim());
        }
        if (moduleFilters.entityType.trim()) {
          params.set("entityType", moduleFilters.entityType.trim());
        }
        if (moduleFilters.validationStatus.trim()) {
          params.set("validationStatus", moduleFilters.validationStatus.trim());
        }

        const response = await getJson(`/admin/registries/${activeRegistryType}?${params.toString()}`);
        if (!active) {
          return;
        }
        setRegistryMap((current) => ({ ...current, [activeRegistryType]: response.data }));
        setFormState((current) => (Object.keys(current).length ? current : buildInitialForm(response.data)));
      } catch (loadError) {
        if (active) {
          setError(loadError.message);
        }
      }
    }

    loadRegistry();
    return () => {
      active = false;
    };
  }, [activeRegistryType, page, moduleSearch, moduleFilters, globalSearch]);

  const pagedRecords = activeRegistry?.records || [];
  const totalPages = Math.max(1, Math.ceil((activeRegistry?.total || 0) / (activeRegistry?.page_size || PAGE_SIZE)));

  const filteredTransactions = useMemo(() => {
    return activity.filter((item) => {
      if (globalSearch.trim()) {
        const queryText = globalSearch.trim().toLowerCase();
        if (!JSON.stringify(item).toLowerCase().includes(queryText)) {
          return false;
        }
      }

      if (transactionFilters.entityType && item.entity_type !== transactionFilters.entityType) {
        return false;
      }

      if (transactionFilters.status) {
        const statusValue = `${item.new_status || ""} ${item.previous_status || ""}`.toLowerCase();
        if (!statusValue.includes(transactionFilters.status.toLowerCase())) {
          return false;
        }
      }

      if (transactionFilters.dateFrom && String(item.timestamp).slice(0, 10) < transactionFilters.dateFrom) {
        return false;
      }

      if (transactionFilters.dateTo && String(item.timestamp).slice(0, 10) > transactionFilters.dateTo) {
        return false;
      }

      return true;
    });
  }, [activity, globalSearch, transactionFilters]);

  const filteredAudit = useMemo(() => {
    if (!globalSearch.trim()) {
      return history;
    }
    const queryText = globalSearch.trim().toLowerCase();
    return history.filter((item) => JSON.stringify(item).toLowerCase().includes(queryText));
  }, [history, globalSearch]);

  const startCreate = () => {
    setEditingRecordId("");
    setFormOpen(true);
    setFormState(buildInitialForm(activeRegistry));
    setMessage("");
  };

  const startEdit = (record) => {
    if (!activeRegistry) {
      return;
    }
    setEditingRecordId(String(record[activeRegistry.id_field]));
    setFormOpen(true);
    setFormState(
      activeRegistry.fields.reduce((accumulator, field) => {
        accumulator[field.key] = normalizeFieldValue(record[field.key], field.type);
        return accumulator;
      }, {})
    );
  };

  const saveRecord = async (event) => {
    event.preventDefault();
    if (!activeRegistryType) {
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");
    try {
      if (editingRecordId) {
        await patchJson(`/admin/registries/${activeRegistryType}/${editingRecordId}`, formState);
        setMessage("Registro actualizado.");
      } else {
        await postJson(`/admin/registries/${activeRegistryType}`, formState);
        setMessage("Registro creado.");
      }

      await refreshRegistry(activeRegistryType);
      setEditingRecordId("");
      setFormOpen(false);
      setFormState(buildInitialForm(activeRegistry));
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteRecord = async (recordId) => {
    const confirmed = window.confirm("¿Querés borrar este registro?");
    if (!confirmed || !activeRegistryType) {
      return;
    }

    setError("");
    setMessage("");
    try {
      await deleteJson(`/admin/registries/${activeRegistryType}/${recordId}`);
      await refreshRegistry(activeRegistryType);
      setMessage("Registro eliminado.");
      if (editingRecordId === String(recordId)) {
        setEditingRecordId("");
        setFormOpen(false);
        setFormState(buildInitialForm(activeRegistry));
      }
      if (detail?.identifier === String(recordId)) {
        setDetail(null);
        setDetailOpen(false);
      }
    } catch (deleteError) {
      setError(deleteError.message);
    }
  };

  const openDetail = async (record) => {
    if (!activeRegistryType || !activeRegistry) {
      return;
    }

    setDetailLoading(true);
    setDetailOpen(true);
    setDetailTab("summary");
    try {
      const identifier = record[activeRegistry.id_field];
      const response = await getJson(`/admin/entities/${activeRegistryType}/${identifier}`);
      setDetail(response.data);
    } catch (detailError) {
      setError(detailError.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshRegistry = async (entityType) => {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(PAGE_SIZE),
    });
    if (globalSearch.trim()) {
      params.set("search", globalSearch.trim());
    }
    if (moduleSearch.trim()) {
      params.set("search", moduleSearch.trim());
    }
    if (moduleFilters.status.trim()) {
      params.set("status", moduleFilters.status.trim());
    }
    if (moduleFilters.company.trim()) {
      params.set("company", moduleFilters.company.trim());
    }
    if (moduleFilters.documentType.trim()) {
      params.set("documentType", moduleFilters.documentType.trim());
    }
    if (moduleFilters.entityType.trim()) {
      params.set("entityType", moduleFilters.entityType.trim());
    }
    if (moduleFilters.validationStatus.trim()) {
      params.set("validationStatus", moduleFilters.validationStatus.trim());
    }
    const response = await getJson(`/admin/registries/${entityType}?${params.toString()}`);
    setRegistryMap((current) => ({ ...current, [entityType]: response.data }));
  };

  const navigateToAdminEntity = useCallback(async ({ registryType, identifier, moduleSearchValue = "", source = "" }) => {
    if (!registryType || identifier === null || identifier === undefined || identifier === "") {
      return;
    }

    const module = MODULES.find((item) => item.registry === registryType);
    pendingNavigationRef.current = { registryType, identifier };
    if (module) {
      setActiveModule(module.key);
    }
    setPage(1);
    setFormOpen(false);
    setEditingRecordId("");
    setModuleFilters({ status: "", company: "", documentType: "", entityType: "", validationStatus: "" });
    setModuleSearch(moduleSearchValue || String(identifier));
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailTab("summary");
    setError("");

    try {
      const query = source ? `?source=${encodeURIComponent(source)}` : "";
      const response = await getJson(`/admin/entities/${registryType}/${identifier}${query}`);
      setDetail(response.data);
    } catch (detailError) {
      setError(detailError.message || "No pudimos abrir la entidad.");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  return (
    <main className="admin-shell">
      <aside className="admin-app-sidebar">
        <div className="admin-app-sidebar-brand">
          <span className="eyebrow">Backoffice</span>
          <h1>Camionero Admin</h1>
        </div>
        <nav className="admin-app-nav">
          {MODULES.map((module) => {
            const registrySummary = catalog.find((item) => item.entity_type === module.registry);
            return (
              <button
                key={module.key}
                className={`admin-app-nav-item ${activeModule === module.key ? "admin-app-nav-item-active" : ""}`}
                type="button"
                onClick={() => setActiveModule(module.key)}
              >
                <span>{module.label}</span>
                {registrySummary ? <small>{registrySummary.total}</small> : null}
              </button>
            );
          })}
        </nav>
      </aside>

      <section className="admin-app-main">
        <header className="admin-topbar">
          <label className="field admin-search-field">
            <span>Búsqueda global</span>
            <input
              type="text"
              value={globalSearch}
              onChange={(event) => setGlobalSearch(event.target.value)}
              placeholder="Buscar por entidad, CUIT, dominio, email o acción..."
            />
          </label>
          <div className="admin-topbar-actions">
            <button className="secondary-button" type="button">
              Notificaciones
            </button>
	            <button className="secondary-button" type="button">
	              Admin
	            </button>
	            <button className="secondary-button" type="button" onClick={onLogout}>
	              Logout
	            </button>
          </div>
        </header>

        <div className="admin-content-stack">
          {loading ? <ProcessingPanel title="Cargando backoffice" message="Estamos preparando métricas, registros y trazabilidad." /> : null}
          {error ? <FeedbackPanel message={error} error /> : null}
          {message ? <FeedbackPanel message={message} /> : null}

          {!loading && activeModule === "dashboard" ? (
            <DashboardHome metrics={metrics} activity={activity} />
          ) : null}

        {!loading && activeModule === "document_policies" ? (
          <section className="document-policies-panel">
          <header className="document-policies-header">
            <div>
              <h2>Documentos autorizados</h2>
              <p className="admin-panel-copy">
                Listado maestro de documentos actuales por entidad. Agregá nuevos registros para que las operaciones los puedan pedir.
              </p>
            </div>
          </header>
          {consumerCompaniesLoading ? (
            <ProcessingPanel title="Cargando empresas" message="Listando empresas consumidoras." />
          ) : null}
          {consumerCompaniesError ? <FeedbackPanel message={consumerCompaniesError} error /> : null}
          {documentLibraryLoading ? (
            <ProcessingPanel title="Cargando catálogo" message="Trayendo los documentos disponibles." />
          ) : null}
          {documentLibraryError ? <FeedbackPanel message={documentLibraryError} error /> : null}
          {documentLibrary.length ? (
            <div className="document-policies-grid">
              <DocumentCatalogViewer
                groupedLibrary={groupedDocumentLibrary}
                entityType={selectedEntityForCatalog}
                onSelectEntity={setSelectedEntityForCatalog}
              />
            </div>
          ) : null}
          <DocumentRequirementForm onSaved={refreshDocuments} library={documentLibrary} />
          {requirementsLoading ? (
            <ProcessingPanel title="Cargando documentos" message="Traemos el catálogo de documentos autorizados." />
          ) : null}
          {requirementsError ? <FeedbackPanel message={requirementsError} error /> : null}
          {!requirementsLoading && !requirementsError ? (
            <DocumentPoliciesTable
              requirements={requirements}
              library={documentLibrary}
              consumers={consumerCompanies}
              onDelete={refreshDocuments}
            />
          ) : null}
          </section>
        ) : null}

          {!loading && activeRegistry && activeModule !== "dashboard" && activeModule !== "transactions" && activeModule !== "audit_log" && activeModule !== "settings" ? (
            <section
              className={`admin-module-layout${
                detailPanelVisible
                  ? " admin-module-layout--detail-expanded"
                  : " admin-module-layout--no-detail"
              }`}
            >
              <section className="detail-panel">
                <div className="detail-panel-header">
                  <div>
                    <h2>{activeRegistry.title}</h2>
                    <p className="admin-panel-copy">Vista tabular con filtros, acciones y acceso a detalle histórico.</p>
                  </div>
                  {!activeRegistry.read_only && !activeRegistry.create_disabled ? (
                    <button className="submit-button" type="button" onClick={startCreate}>
                      Nuevo registro
                    </button>
                  ) : null}
                </div>

                <div className="admin-table-toolbar">
                  <label className="field">
                    <span>Buscar en {activeRegistry.title.toLowerCase()}</span>
                    <input
                      type="text"
                      value={moduleSearch}
                      onChange={(event) => setModuleSearch(event.target.value)}
                      placeholder="Filtrar resultados..."
                    />
                  </label>
                  <label className="field">
                    <span>Estado</span>
                    <input
                      type="text"
                      value={moduleFilters.status}
                      onChange={(event) => {
                        setPage(1);
                        setModuleFilters((current) => ({ ...current, status: event.target.value }));
                      }}
                      placeholder="validado, observado, conforme..."
                    />
                  </label>
                  <label className="field">
                    <span>Empresa / CUIT</span>
                    <input
                      type="text"
                      value={moduleFilters.company}
                      onChange={(event) => {
                        setPage(1);
                        setModuleFilters((current) => ({ ...current, company: event.target.value }));
                      }}
                      placeholder="CUIT o company id..."
                    />
                  </label>
                {activeRegistryType === "documentos" ? (
                  <>
                    <label className="field">
                      <span>Tipo de documento</span>
                      <input
                        type="text"
                        value={moduleFilters.documentType}
                        onChange={(event) => {
                          setPage(1);
                          setModuleFilters((current) => ({ ...current, documentType: event.target.value }));
                        }}
                        placeholder="licencia, seguro..."
                      />
                    </label>
                    <label className="field">
                      <span>Tipo de entidad</span>
                      <input
                        type="text"
                        value={moduleFilters.entityType}
                        onChange={(event) => {
                          setPage(1);
                          setModuleFilters((current) => ({ ...current, entityType: event.target.value }));
                        }}
                        placeholder="chofer, unidad..."
                      />
                    </label>
                    <label className="field">
                      <span>Estado de validación</span>
                      <input
                        type="text"
                        value={moduleFilters.validationStatus}
                        onChange={(event) => {
                          setPage(1);
                          setModuleFilters((current) => ({ ...current, validationStatus: event.target.value }));
                        }}
                        placeholder="observado, aprobado..."
                      />
                    </label>
                  </>
                ) : null}
                </div>

                {!activeRegistry.read_only && formOpen ? (
                  <form className="admin-form" onSubmit={saveRecord}>
                    <div className="admin-form-grid">
                      {activeRegistry.fields.map((field) => (
                        <label className="field" key={field.key}>
                          <span>{field.label}</span>
                          {field.type === "textarea" ? (
                            <textarea
                              rows="3"
                              value={formState[field.key] || ""}
                              onChange={(event) => setFormState((current) => ({ ...current, [field.key]: event.target.value }))}
                              disabled={field.readOnly}
                            />
                          ) : field.type === "select" ? (
                            <select
                              value={formState[field.key] || ""}
                              onChange={(event) => setFormState((current) => ({ ...current, [field.key]: event.target.value }))}
                              disabled={field.readOnly}
                            >
                              <option value="">Seleccionar</option>
                              {(field.options || []).map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          ) : field.type === "boolean" ? (
                            <select
                              value={formState[field.key] || "false"}
                              onChange={(event) => setFormState((current) => ({ ...current, [field.key]: event.target.value }))}
                              disabled={field.readOnly}
                            >
                              <option value="true">Sí</option>
                              <option value="false">No</option>
                            </select>
                          ) : (
                            <input
                              type={field.type === "date" ? "date" : field.type === "number" ? "number" : "text"}
                              value={formState[field.key] || ""}
                              onChange={(event) => setFormState((current) => ({ ...current, [field.key]: event.target.value }))}
                              disabled={field.readOnly || (Boolean(editingRecordId) && field.key === activeRegistry.id_field)}
                            />
                          )}
                        </label>
                      ))}
                    </div>
                    <div className="admin-form-actions">
                      <button className="submit-button" type="submit" disabled={saving}>
                        {saving ? "Guardando..." : editingRecordId ? "Actualizar" : "Crear"}
                      </button>
                      <button
                        className="secondary-button"
                        type="button"
                        onClick={() => {
                          setEditingRecordId("");
                          setFormOpen(false);
                          setFormState(buildInitialForm(activeRegistry));
                        }}
                      >
                        Cerrar
                      </button>
                    </div>
                  </form>
                ) : null}

                <div className="admin-records-table-wrapper">
                  <table className="admin-records-table">
                    <thead>
                      <tr>
                        {visibleRegistryFields.map((field) => (
                          <th key={field.key}>{field.label}</th>
                        ))}
                        <th>Fecha</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedRecords.map((record) => (
                        <tr key={`${activeRegistryType}-${record[activeRegistry.id_field]}`}>
                          {visibleRegistryFields.map((field) => (
                            <td key={field.key}>{displayRecordValue(record[field.key])}</td>
                          ))}
                          <td>{formatDateTime(record.updated_at || record.created_at)}</td>
                          <td>
                            <div className="admin-record-actions">
                              <button className="admin-inline-action" type="button" onClick={() => openDetail(record)}>
                                Detalle
                              </button>
                              {!activeRegistry.read_only ? (
                                <>
                                  <button className="admin-inline-action" type="button" onClick={() => startEdit(record)}>
                                    Editar
                                  </button>
                                  <button
                                    className="admin-inline-action admin-inline-action-danger"
                                    type="button"
                                    onClick={() => deleteRecord(record[activeRegistry.id_field])}
                                  >
                                    Borrar
                                  </button>
                                </>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 ? (
                  <div className="admin-pagination">
                    <span>
                      Página {activeRegistry.page || page} de {totalPages} · {activeRegistry.total || 0} registros
                    </span>
                    <div className="admin-record-actions">
                      <button className="secondary-button" type="button" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>
                        Anterior
                      </button>
                      <button
                        className="secondary-button"
                        type="button"
                        disabled={page >= totalPages}
                        onClick={() => setPage((current) => current + 1)}
                      >
                        Siguiente
                      </button>
                    </div>
                  </div>
                ) : null}
              </section>

              {detailOpen ? (
                <section className="detail-panel admin-detail-panel">
                  <div className="detail-panel-header">
                    <div>
                      <h2>{detail ? `${detail.title} · ${detail.identifier}` : "Detalle"}</h2>
                      <p className="admin-panel-copy">Resumen, historial, trazabilidad y archivos históricos de la entidad seleccionada.</p>
                    </div>
                    <button
                      className="secondary-button"
                      type="button"
                      onClick={() => {
                        setDetailOpen(false);
                        setDetail(null);
                      }}
                    >
                      Cerrar
                    </button>
                  </div>

                  {detailLoading ? <ProcessingPanel title="Cargando detalle" message="Estamos trayendo el histórico completo de la entidad." /> : null}

                  {!detailLoading && !detail ? <p className="status-message">Cargando detalle...</p> : null}

                  {detail ? (
                    <>
                      <div className="admin-tab-group">
                        {[
                          { key: "summary", label: "Summary" },
                          { key: "history", label: "History" },
                          { key: "audit", label: "Audit trail" },
                          { key: "files", label: "Historical files" },
                        ].map((tab) => (
                          <button
                            key={tab.key}
                            className={`secondary-button ${detailTab === tab.key ? "admin-tab-active" : ""}`}
                            type="button"
                            onClick={() => setDetailTab(tab.key)}
                          >
                            {tab.label}
                          </button>
                        ))}
                      </div>

                      {detailTab === "summary" ? (
                        <>
                          <EntitySummary detail={detail} onNavigate={navigateToAdminEntity} />
                          {detail?.entity_type === "documentos" && detail?.summary?.record_source === "documentos" ? (
                            <DocumentStatusEditor detail={detail} onUpdated={() => openDetail(detail.summary)} />
                          ) : null}
                        </>
                      ) : null}
                      {detailTab === "history" ? <HistoryFeed items={detail.history} emptyMessage="No hay historial registrado." /> : null}
                      {detailTab === "audit" ? <AuditFeed items={detail.audit} /> : null}
                      {detailTab === "files" ? <FilesFeed items={detail.files} /> : null}
                    </>
                  ) : null}
                </section>
              ) : null}
            </section>
          ) : null}

          {!loading && activeModule === "transactions" ? (
            <TransactionsPanel
              items={filteredTransactions}
              filters={transactionFilters}
              setFilters={setTransactionFilters}
              selectedTransaction={selectedTransaction}
              onSelectTransaction={setSelectedTransaction}
              onCloseTransaction={() => setSelectedTransaction(null)}
            />
          ) : null}

          {!loading && activeModule === "audit_log" ? <AuditLogPanel items={filteredAudit} /> : null}
          {!loading && activeModule === "settings" ? <SettingsPanel /> : null}
        </div>
      </section>
    </main>
  );
}

function DashboardHome({ metrics, activity }) {
  const cards = [
    { label: "Choferes totales", value: metrics?.total_drivers || 0 },
    { label: "Vehículos activos", value: metrics?.active_vehicles || 0 },
    { label: "Documentos pendientes", value: metrics?.pending_documents || 0 },
    { label: "Documentos rechazados", value: metrics?.rejected_documents || 0 },
    { label: "Operaciones en curso", value: metrics?.operations_in_progress || 0 },
    { label: "Operaciones completadas", value: metrics?.operations_completed || 0 },
  ];

  return (
    <>
      <section className="admin-kpi-grid">
        {cards.map((card) => (
          <article className="detail-panel admin-kpi-card" key={card.label}>
            <span>{card.label}</span>
            <strong>{card.value}</strong>
          </article>
        ))}
      </section>

      <section className="admin-home-grid">
        <section className="detail-panel">
          <div className="detail-panel-header">
            <div>
              <h2>Alertas recientes</h2>
              <p className="admin-panel-copy">Alertas operativas y documentales para priorizar seguimiento.</p>
            </div>
          </div>
          <div className="admin-feed-list">
            {(metrics?.recent_alerts || []).map((item, index) => (
              <article className="admin-feed-card" key={`${item.alert_type}-${item.timestamp}-${index}`}>
                <div className="admin-feed-card-top">
                  <strong>{item.title}</strong>
                  <StatusBadge
                    label={item.severity === "high" ? "Crítico" : item.severity === "medium" ? "Atención" : "Info"}
                    tone={item.severity === "high" ? "vencido" : item.severity === "medium" ? "observed" : "vigente"}
                  />
                </div>
                <p>{item.description}</p>
                <span>{formatDateTime(item.timestamp)}</span>
              </article>
            ))}
          </div>
        </section>

        <section className="detail-panel">
          <div className="detail-panel-header">
            <div>
              <h2>Recent transactions</h2>
              <p className="admin-panel-copy">Feed operativo en tiempo real, ordenado por el evento más reciente.</p>
            </div>
          </div>
          <TransactionTable items={(metrics?.recent_transactions || activity || []).slice(0, 12)} compact />
        </section>
      </section>
    </>
  );
}

function TransactionsPanel({ items, filters, setFilters, selectedTransaction, onSelectTransaction, onCloseTransaction }) {
  return (
    <section className="admin-module-layout">
      <section className="detail-panel">
        <div className="detail-panel-header">
          <div>
            <h2>Transactions</h2>
            <p className="admin-panel-copy">Monitor operativo con actualización automática y foco en los eventos más nuevos.</p>
          </div>
        </div>
        <div className="admin-transaction-filters">
          <label className="field">
            <span>Entity type</span>
            <input value={filters.entityType} onChange={(event) => setFilters((current) => ({ ...current, entityType: event.target.value }))} />
          </label>
          <label className="field">
            <span>Status</span>
            <input value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))} />
          </label>
          <label className="field">
            <span>From</span>
            <input type="date" value={filters.dateFrom} onChange={(event) => setFilters((current) => ({ ...current, dateFrom: event.target.value }))} />
          </label>
          <label className="field">
            <span>To</span>
            <input type="date" value={filters.dateTo} onChange={(event) => setFilters((current) => ({ ...current, dateTo: event.target.value }))} />
          </label>
        </div>
        <TransactionTable items={items} onRowClick={onSelectTransaction} />
      </section>

      <section className="detail-panel admin-detail-panel">
        <div className="detail-panel-header">
          <div>
            <h2>{selectedTransaction ? "Event detail" : "Event detail"}</h2>
            <p className="admin-panel-copy">Detalle del evento, estados involucrados y payload operativo.</p>
          </div>
          {selectedTransaction ? (
            <button className="secondary-button" type="button" onClick={onCloseTransaction}>
              Cerrar
            </button>
          ) : null}
        </div>
        {!selectedTransaction ? <p className="status-message">Elegí una transacción para ver el detalle completo.</p> : null}
        {selectedTransaction ? (
          <div className="admin-feed-list">
            <article className="admin-feed-card">
              <div className="admin-feed-card-top">
                <strong>{selectedTransaction.action}</strong>
                <StatusBadge label={selectedTransaction.entity_type} tone="observed" />
              </div>
              <p>ID entidad: {displayRecordValue(selectedTransaction.entity_id)}</p>
              <span>{formatDateTime(selectedTransaction.timestamp)}</span>
            </article>
            <article className="admin-feed-card">
              <strong>Transición</strong>
              <p>Anterior: {displayRecordValue(selectedTransaction.previous_status)}</p>
              <p>Nuevo: {displayRecordValue(selectedTransaction.new_status)}</p>
              <p>Actor: {displayRecordValue(selectedTransaction.actor)}</p>
              <p>Operación relacionada: {displayRecordValue(selectedTransaction.related_operation_id)}</p>
            </article>
            <article className="admin-feed-card">
              <strong>Payload</strong>
              <pre className="admin-payload-preview">{JSON.stringify(selectedTransaction.payload || selectedTransaction, null, 2)}</pre>
            </article>
          </div>
        ) : null}
      </section>
    </section>
  );
}

function TransactionTable({ items, compact = false, onRowClick = null }) {
  return (
    <div className="admin-records-table-wrapper">
      <table className={`admin-records-table ${compact ? "admin-records-table-compact" : ""}`}>
        <thead>
          <tr>
            <th>Timestamp</th>
            <th>Entity type</th>
            <th>Entity id</th>
            <th>Action</th>
            <th>Previous</th>
            <th>New</th>
            <th>Actor</th>
            <th>Operation</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => (
            <tr
              key={`${item.entity_type}-${item.entity_id}-${item.timestamp}-${index}`}
              className={onRowClick ? "admin-clickable-row" : ""}
              onClick={onRowClick ? () => onRowClick(item) : undefined}
            >
              <td>{formatDateTime(item.timestamp)}</td>
              <td>{displayRecordValue(item.entity_type)}</td>
              <td>{displayRecordValue(item.entity_id)}</td>
              <td>{displayRecordValue(item.action)}</td>
              <td>{displayRecordValue(item.previous_status)}</td>
              <td>{displayRecordValue(item.new_status)}</td>
              <td>{displayRecordValue(item.actor)}</td>
              <td>{displayRecordValue(item.related_operation_id)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuditLogPanel({ items }) {
  return (
    <section className="detail-panel">
      <div className="detail-panel-header">
        <div>
          <h2>Audit log</h2>
          <p className="admin-panel-copy">Registro inmutable de transiciones, controles y actividad histórica vinculada a entidades y operaciones.</p>
        </div>
      </div>
      <AuditFeed items={items} />
    </section>
  );
}

function SettingsPanel() {
  return (
    <section className="detail-panel">
      <div className="detail-panel-header">
        <div>
          <h2>Settings</h2>
          <p className="admin-panel-copy">Espacio reservado para configuraciones globales del backoffice y futuras políticas operativas.</p>
        </div>
      </div>
      <div className="portal-list">
        <article className="portal-list-card">
          <strong>Entorno</strong>
          <p>Este módulo queda listo para crecer con reglas de retención, alertas y configuraciones del monitoreo.</p>
        </article>
      </div>
    </section>
  );
}

function EntitySummary({ detail, onNavigate }) {
  const summaryEntries = Object.entries(detail.summary || {});

  return (
    <div className="admin-detail-summary">
      <div className="admin-detail-grid">
        {summaryEntries.map(([key, value]) => (
          <div className="portal-list-card" key={key}>
            <strong>{humanizeKey(key)}</strong>
            <span>{renderSummaryValue(detail, key, value, onNavigate)}</span>
          </div>
        ))}
      </div>

      {detail.related && Object.keys(detail.related).length ? (
        <div className="admin-detail-related">
          {Object.entries(detail.related).map(([groupKey, items]) => (
            <article className="detail-panel" key={groupKey}>
              <div className="detail-panel-header">
                <div>
                  <h3>{humanizeKey(groupKey)}</h3>
                </div>
              </div>
              {groupKey === "operation_requirements" ? (
                <OperationRequirementsList items={items} onNavigate={onNavigate} />
              ) : (
                <RelatedItemsList groupKey={groupKey} items={items} onNavigate={onNavigate} />
              )}
            </article>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function RelatedItemsList({ groupKey, items, onNavigate }) {
  const [query, setQuery] = useState("");
  const trimmedQuery = query.trim().toLowerCase();
  const filteredItems = useMemo(() => {
    if (groupKey !== "operations" || !trimmedQuery) {
      return items || [];
    }
    return (items || []).filter((item) => {
      const operationId = String(item.id || "").trim();
      const operationLabel = formatOperationReference(item.id);
      const haystack = [
        operationId,
        operationLabel,
        item.compliance_status,
        item.status,
        item.created_at,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(trimmedQuery);
    });
  }, [groupKey, items, trimmedQuery]);

  return (
    <div className="portal-list">
      {groupKey === "operations" ? (
        <label className="field">
          <span>Buscar operación</span>
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="OP-..., estado o fecha..."
          />
        </label>
      ) : null}
      {filteredItems.length ? (
        filteredItems.map((item, index) => (
          <article className="portal-list-card" key={`${groupKey}-${index}`}>
            <strong>{renderRelatedPrimary(groupKey, item, onNavigate)}</strong>
            <p>{getRelatedItemSecondary(groupKey, item)}</p>
            <span>{getRelatedItemMeta(groupKey, item)}</span>
          </article>
        ))
      ) : (
        <p className="status-message">{groupKey === "operations" && trimmedQuery ? "No encontramos esa operación." : "Sin relaciones activas."}</p>
      )}
    </div>
  );
}

function HistoryFeed({ items, emptyMessage }) {
  if (!items?.length) {
    return <p className="status-message">{emptyMessage}</p>;
  }

  return (
    <div className="admin-feed-list">
      {items.map((item, index) => (
        <article className="admin-feed-card" key={`${item.timestamp}-${index}`}>
          <div className="admin-feed-card-top">
            <strong>{displayRecordValue(item.current_status || item.action || "Cambio")}</strong>
            <span>{formatDateTime(item.timestamp)}</span>
          </div>
          <pre className="admin-payload-preview">{JSON.stringify(item.payload || item, null, 2)}</pre>
        </article>
      ))}
    </div>
  );
}

function AuditFeed({ items }) {
  if (!items?.length) {
    return <p className="status-message">No hay eventos de auditoría registrados.</p>;
  }

  return (
    <div className="admin-feed-list">
      {items.map((item, index) => (
        <article className="admin-feed-card admin-history-card" key={`${item.timestamp || item.created_at}-${index}`}>
          <div className="admin-feed-card-top">
            <strong>{displayRecordValue(item.action || item.accion)}</strong>
            <StatusBadge label={displayRecordValue(item.entity_type || item.entidad_tipo)} tone="observed" />
          </div>
          <p>{displayRecordValue(item.entity_id || item.entidad_id)}</p>
          <span>{formatDateTime(item.timestamp || item.created_at)}</span>
          <pre className="admin-payload-preview">{JSON.stringify(item.detalle || item.payload || item, null, 2)}</pre>
        </article>
      ))}
    </div>
  );
}

function FilesFeed({ items }) {
  const [openingId, setOpeningId] = useState("");
  const [error, setError] = useState("");

  const openFile = async (item) => {
    const downloadId = item?.download_id;
    const downloadKind = item?.download_kind || "document";
    const downloadKey = item?.download_key;
    if (!downloadId && !downloadKey) {
      return;
    }
    setOpeningId(String(item.id || downloadId));
    setError("");
    try {
      const endpoint =
        downloadKind === "signature"
          ? `/signatures/documents/${downloadId}/url`
          : downloadKind === "admin_s3_key"
            ? `/admin/files/url?key=${encodeURIComponent(downloadKey)}`
          : `/documentos/${downloadId}/url`;
      const response = await getJson(endpoint);
      window.open(response.data.url, "_blank", "noopener,noreferrer");
    } catch (openError) {
      setError(openError.message || "No pudimos abrir el archivo.");
    } finally {
      setOpeningId("");
    }
  };

  if (!items?.length) {
    return <p className="status-message">No hay archivos históricos para esta entidad.</p>;
  }

  return (
    <div className="admin-feed-list">
      {error ? <p className="status-message">{error}</p> : null}
      {items.map((item, index) => (
        <article className="admin-feed-card" key={`${item.id || item.s3_key}-${index}`}>
          <div className="admin-feed-card-top">
            {item.download_id || item.download_key ? (
              <button className="admin-inline-link" type="button" onClick={() => openFile(item)}>
                {displayRecordValue(item.document_name || item.tipo_documento || item.version_type || item.s3_key)}
              </button>
            ) : (
              <strong>{displayRecordValue(item.document_name || item.tipo_documento || item.version_type || item.s3_key)}</strong>
            )}
            <span>{formatDate(item.created_at)}</span>
          </div>
          <p>{displayRecordValue(item.mime_type || item.version_type || item.status)}</p>
          {item.download_id || item.download_key ? (
            <div className="admin-feed-card-actions">
              <button className="secondary-button" type="button" onClick={() => openFile(item)} disabled={openingId === String(item.id || item.download_id)}>
                {openingId === String(item.id || item.download_id) ? "Abriendo..." : "Ver archivo"}
              </button>
            </div>
          ) : null}
        </article>
      ))}
    </div>
  );
}

function OperationRequirementsList({ items, onNavigate }) {
  const [openingId, setOpeningId] = useState("");
  const [error, setError] = useState("");

  const openFile = async (item) => {
    const downloadId = item?.download_id;
    const downloadKind = item?.download_kind || "document";
    const downloadKey = item?.download_key;
    if (!downloadId && !downloadKey) {
      return;
    }
    setOpeningId(String(item.id || downloadId));
    setError("");
    try {
      const response =
        downloadKind === "admin_s3_key"
          ? await getJson(`/admin/files/url?key=${encodeURIComponent(downloadKey)}`)
          : await getJson(`/documentos/${downloadId}/url`);
      window.open(response.data.url, "_blank", "noopener,noreferrer");
    } catch (openError) {
      setError(openError.message || "No pudimos abrir el archivo.");
    } finally {
      setOpeningId("");
    }
  };

  if (!items?.length) {
    return <p className="status-message">No hay requirements documentales para esta operación.</p>;
  }

  return (
    <div className="portal-list">
      {error ? <p className="status-message">{error}</p> : null}
      {items.map((item, index) => {
        const canOpen = Boolean(item.download_id || item.download_key);
        const opening = openingId === String(item.id || item.download_id);
        const documentLabel = item.document_type || item.nombre_archivo || item.document_id;
        const documentSearchValue = String(item.nombre_archivo || item.document_type || item.document_id || "").trim();
        const entityTarget = resolveOperationRequirementEntityTarget(item);
        const documentTarget = item.document_id
          ? { registryType: "documentos", identifier: item.document_id, moduleSearchValue: documentSearchValue || String(item.document_id) }
          : null;
        return (
          <article className="portal-list-card" key={`${item.id || item.document_type}-${index}`}>
            <strong>
              {documentTarget && typeof onNavigate === "function" ? (
                <button className="admin-inline-link" type="button" onClick={() => onNavigate(documentTarget)}>
                  {displayRecordValue(documentLabel)}
                </button>
              ) : canOpen ? (
                <button className="admin-inline-link" type="button" onClick={() => openFile(item)} disabled={opening}>
                  {displayRecordValue(documentLabel)}
                </button>
              ) : (
                displayRecordValue(documentLabel)
              )}
            </strong>
            <p>
              {entityTarget && typeof onNavigate === "function" ? (
                <button className="admin-inline-link" type="button" onClick={() => onNavigate(entityTarget)}>
                  {[humanizeKey(item.entity_type), displayRecordValue(item.entity_id)]
                    .filter((value) => value && value !== "-")
                    .join(" · ")}
                </button>
              ) : (
                [humanizeKey(item.entity_type), displayRecordValue(item.entity_id)]
                  .filter((value) => value && value !== "-")
                  .join(" · ")
              )}
            </p>
            <p>
              {[
                `Aceptación: ${displayRecordValue(item.result)}`,
                `Doc actual: ${displayRecordValue(item.document_status || item.evidence_document_state || "sin documento")}`,
              ].join(" · ")}
            </p>
            {item.validation_reason ? <span>{displayRecordValue(item.validation_reason)}</span> : null}
            <div className="admin-feed-card-actions">
              {canOpen ? (
                <button className="secondary-button" type="button" onClick={() => openFile(item)} disabled={opening}>
                  {opening ? "Abriendo..." : "Ver documento"}
                </button>
              ) : null}
              {documentTarget && typeof onNavigate === "function" ? (
                <button className="secondary-button" type="button" onClick={() => onNavigate(documentTarget)}>
                  Ver detalle
                </button>
              ) : null}
            </div>
          </article>
        );
      })}
    </div>
  );
}

const DOCUMENT_STATUS_OPTIONS = [
  "vigente",
  "pendiente",
  "pendiente_foto",
  "observado",
  "rechazado",
  "expired",
  "pending_validation",
  "pending_signature",
];

const VALIDATION_STATUS_OPTIONS = [
  "validated",
  "rejected",
  "observed",
  "pending_validation",
  "pending_signature",
  "access_denied",
];

function DocumentStatusEditor({ detail, onUpdated }) {
  const summary = detail?.summary || {};
  const validationEntry = useMemo(
    () => (detail?.files || []).find((item) => item.version_type === "document_validation"),
    [detail]
  );
  const [documentState, setDocumentState] = useState(summary.estado || "");
  const [validationStatus, setValidationStatus] = useState(validationEntry?.status || "");
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    setDocumentState(summary.estado || "");
    setValidationStatus(validationEntry?.status || "");
    setFeedback("");
  }, [summary.estado, validationEntry?.status]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!summary.id) {
      return;
    }

    const trimmedState = documentState.trim();
    const payload = {};
    if (trimmedState) {
      payload.estado = trimmedState;
    }
    payload.validation = {
      status: validationStatus || undefined,
    };

    setSaving(true);
    setFeedback("");
    try {
      await patchJson(`/admin/documents/${summary.id}/status`, payload);
      setFeedback("El estado se actualizó correctamente.");
      if (onUpdated) {
        onUpdated();
      }
    } catch (updateError) {
      setFeedback(updateError.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="detail-panel admin-document-status-panel">
      <div className="detail-panel-header">
        <div>
          <h3>Editor de estado documental</h3>
          <p className="admin-panel-copy">Modifica manualmente el estado o la validación para pruebas.</p>
        </div>
      </div>
      <form className="admin-form" onSubmit={handleSubmit}>
        <div className="admin-form-grid">
          <label className="field">
            <span>Estado del documento</span>
            <select value={documentState} onChange={(event) => setDocumentState(event.target.value)}>
              <option value="">-- mantener --</option>
              {DOCUMENT_STATUS_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Estado de validación</span>
            <select value={validationStatus} onChange={(event) => setValidationStatus(event.target.value)}>
              <option value="">-- mantener --</option>
              {VALIDATION_STATUS_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="admin-form-actions">
          <button className="submit-button" type="submit" disabled={saving}>
            {saving ? "Guardando..." : "Actualizar estado"}
          </button>
          {feedback ? <span className="status-message">{feedback}</span> : null}
        </div>
      </form>
    </section>
  );
}


function DocumentPoliciesTable({ requirements = [], library = [], onDelete }) {
  const entries = useMemo(() => {
    const list = requirements.map((entry) => ({ ...entry }));
    const order = ENTITY_SECTION_ORDER;
    return list.sort((a, b) => {
      const aIndex = order.indexOf(a.entity_type);
      const bIndex = order.indexOf(b.entity_type);
      if (aIndex !== bIndex) {
        const aValue = aIndex === -1 ? Infinity : aIndex;
        const bValue = bIndex === -1 ? Infinity : bIndex;
        return aValue - bValue;
      }
      const aDocumentPriority = getDocumentDisplayPriority(a);
      const bDocumentPriority = getDocumentDisplayPriority(b);
      if (aDocumentPriority !== bDocumentPriority) {
        return aDocumentPriority - bDocumentPriority;
      }
      const aSortOrder = Number.isFinite(Number(a.sort_order)) ? Number(a.sort_order) : 100;
      const bSortOrder = Number.isFinite(Number(b.sort_order)) ? Number(b.sort_order) : 100;
      if (aSortOrder !== bSortOrder) {
        return aSortOrder - bSortOrder;
      }
      if (a.policy_name && b.policy_name && a.policy_name !== b.policy_name) {
        return a.policy_name.localeCompare(b.policy_name);
      }
      return String(a.label || a.document_type || "").localeCompare(String(b.label || b.document_type || ""));
    });
  }, [requirements]);
  const [expandedValidationEntries, setExpandedValidationEntries] = useState({});
  const [validationTextEdits, setValidationTextEdits] = useState({});
  const [editingEntries, setEditingEntries] = useState({});
  const [savingEntries, setSavingEntries] = useState({});

  const getValidationKey = (entry) =>
    entry.requirement_id ? `requirement-${entry.requirement_id}` : `${entry.entity_type}:${entry.document_type}`;
  const getEntryEditKey = (entry) =>
    entry.requirement_id
      ? `requirement-${entry.requirement_id}`
      : entry.policy_document_id
        ? `policy-${entry.policy_document_id}`
        : `${entry.entity_type}:${entry.document_type}`;

  const getEntryBaseText = (entry) => entry.validation_context || entry.notes || "Este documento aún no tiene descripción de validación.";
  const buildEntryEditState = (entry) => ({
    entity_type: entry.entity_type,
    policy_canonical_type: entry.policy_canonical_type || entry.canonical_type || "",
    document_type: entry.document_type || "",
    label: entry.label || entry.document_type || "",
    ai_relevant: entry.ai_relevant !== false,
    required: isPinnedRequiredDocument(entry.entity_type, entry.document_type) ? true : entry.required !== false,
    requires_signature: Boolean(entry.requires_signature),
    access_required: entry.access_required !== false,
    allowed_statuses: Array.isArray(entry.allowed_statuses) ? entry.allowed_statuses.join(", ") : "",
    sort_order: entry.sort_order ?? 100,
  });
  const policyOptionsByEntity = useMemo(() => {
    const map = new Map();
    (library || []).forEach((policy) => {
      const current = map.get(policy.entity_type) || [];
      current.push({
        canonical_type: policy.canonical_type,
        name: policy.name,
      });
      map.set(policy.entity_type, current);
    });
    return map;
  }, [library]);

  useEffect(() => {
    setExpandedValidationEntries({});
    setValidationTextEdits({});
    setEditingEntries({});
    setSavingEntries({});
  }, [requirements]);

  const startEditingEntry = (entry) => {
    const key = getEntryEditKey(entry);
    setEditingEntries((prev) => ({
      ...prev,
      [key]: buildEntryEditState(entry),
    }));
  };

  const cancelEditingEntry = (entry) => {
    const key = getEntryEditKey(entry);
    setEditingEntries((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const changeEditingEntry = (entry, field, value) => {
    const key = getEntryEditKey(entry);
    setEditingEntries((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || buildEntryEditState(entry)),
        [field]: value,
      },
    }));
  };

  const handleValidationTextChange = (entry, nextValue) => {
    const key = getValidationKey(entry);
    const baseText = getEntryBaseText(entry);
    setValidationTextEdits((prev) => ({
      ...prev,
      [key]: {
        value: nextValue,
        dirty: nextValue !== baseText,
      },
    }));
  };

  const toggleValidationContext = async (entry) => {
    const key = getValidationKey(entry);
    const currentlyOpen = Boolean(expandedValidationEntries[key]);
    if (currentlyOpen) {
      const editState = validationTextEdits[key];
      if (editState?.dirty) {
        const save = window.confirm("Guardamos los cambios de validación?");
        if (save) {
          await mutateRequirement(entry, { notes: editState.value });
        }
        setValidationTextEdits((prev) => ({
          ...prev,
          [key]: {
            value: save ? editState.value : getEntryBaseText(entry),
            dirty: false,
          },
        }));
      }
    }
    setExpandedValidationEntries((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const shouldPersistAsGlobalOverride = (updates) =>
    [
      "ai_relevant",
      "policy_canonical_type",
      "label",
      "document_type",
      "required",
      "requires_signature",
      "access_required",
      "allowed_statuses",
      "sort_order",
      "notes",
      "enabled",
    ].some((field) => updates[field] !== undefined);

  const buildCatalogPayload = (entry, updates) => ({
    entity_type: updates.entity_type ?? entry.entity_type,
    policy_canonical_type:
      updates.policy_canonical_type ??
      entry.policy_canonical_type ??
      entry.canonical_type ??
      null,
    document_type: updates.document_type ?? entry.document_type,
    label: updates.label ?? entry.label ?? entry.document_type,
    ai_relevant: updates.ai_relevant ?? entry.ai_relevant,
    required: updates.required ?? entry.required,
    requires_signature: updates.requires_signature ?? entry.requires_signature,
    access_required: updates.access_required ?? entry.access_required,
    allowed_statuses: updates.allowed_statuses ?? entry.allowed_statuses,
    sort_order: updates.sort_order ?? entry.sort_order,
    notes: updates.notes ?? entry.description ?? entry.notes ?? null,
    enabled: updates.enabled ?? entry.enabled,
  });

  const mutateRequirement = async (entry, updates, confirmMessage) => {
    if (confirmMessage && !window.confirm(confirmMessage)) {
      return;
    }
    try {
      if (entry.policy_document_id) {
        await patchJson(`/document-policies/documents/${entry.policy_document_id}`, {
          ...updates,
          ...(updates.enabled !== undefined
            ? { status: updates.enabled ? "active" : "inactive" }
            : {}),
        });
      } else if (shouldPersistAsGlobalOverride(updates)) {
        await postJson("/document-policies/catalog/upsert", buildCatalogPayload(entry, updates));
      } else if (entry.requirement_id) {
        await patchJson(`/document-policies/requirements/${entry.requirement_id}`, updates);
      } else {
        await postJson("/document-policies/catalog/upsert", buildCatalogPayload(entry, updates));
      }
      if (onDelete) {
        onDelete();
      }
    } catch (error) {
      window.alert(error.message);
    }
  };

  const saveEditingEntry = async (entry) => {
    const key = getEntryEditKey(entry);
    const editState = editingEntries[key];
    if (!editState) {
      return;
    }

    const allowedStatuses = String(editState.allowed_statuses || "")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);

    const payload = {
      entity_type: editState.entity_type,
      policy_canonical_type: editState.policy_canonical_type || null,
      document_type: String(editState.document_type || "").trim(),
      label: String(editState.label || "").trim(),
      ai_relevant: Boolean(editState.ai_relevant),
      required: isPinnedRequiredDocument(editState.entity_type, editState.document_type)
        ? true
        : Boolean(editState.required),
      requires_signature: Boolean(editState.requires_signature),
      access_required: Boolean(editState.access_required),
      allowed_statuses: allowedStatuses,
      sort_order: Number(editState.sort_order || 0) || 0,
    };

    if (!payload.document_type) {
      window.alert("El tipo de documento no puede quedar vacío.");
      return;
    }

    if (!payload.label) {
      window.alert("La etiqueta del documento no puede quedar vacía.");
      return;
    }

    setSavingEntries((prev) => ({ ...prev, [key]: true }));
    try {
      await mutateRequirement(entry, payload);
      cancelEditingEntry(entry);
    } finally {
      setSavingEntries((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleDisable = (entry) => {
    mutateRequirement(entry, { enabled: false }, "¿Estás seguro que querés deshabilitar este documento temporalmente?");
  };

  const handleEnable = (entry) => {
    mutateRequirement(entry, { enabled: true }, "¿Querés volver a habilitar este documento en el catálogo?");
  };

  const handleDelete = async (entry) => {
    if (!entry.requirement_id && !entry.policy_document_id) {
      window.alert("Este documento no tiene un registro persistido para eliminar.");
      return;
    }
    if (!window.confirm("¿Eliminar este documento del catálogo?")) {
      return;
    }
    try {
      if (entry.requirement_id) {
        await deleteJson(`/document-policies/requirements/${entry.requirement_id}`);
      } else {
        await deleteJson(`/document-policies/documents/${entry.policy_document_id}`);
      }
      if (onDelete) {
        onDelete();
      }
    } catch (error) {
      window.alert(error.message);
    }
  };

  if (!entries.length) {
    return <p className="status-message">No hay documentos cargados en ninguna política.</p>;
  }

  return (
    <div className="document-policies-table">
      <h3>Documentos activos</h3>
      <table>
        <thead>
            <tr>
              <th>Entidad</th>
              <th>Política</th>
              <th>Documento</th>
              <th>Tipo</th>
              <th>Activo</th>
              <th>Obligatorio</th>
              <th>Estados</th>
              <th>Orden</th>
              <th>Validaciones</th>
              <th>IA</th>
              <th>Firma</th>
              <th>Acceso</th>
              <th>Acciones</th>
            </tr>
        </thead>
        <tbody>
          {entries.map((entry, index) => {
            const statuses = entry.allowed_statuses || [];
            const label = entry.label || entry.document_type;
            const validationKey = getValidationKey(entry);
            const editKey = getEntryEditKey(entry);
            const contextId = `validation-context-${validationKey.replace(/[^a-zA-Z0-9_-]/g, "-") || index}`;
            const isValidationExpanded = Boolean(expandedValidationEntries[validationKey]);
            const baseValidationText = getEntryBaseText(entry);
            const editState = validationTextEdits[validationKey];
            const currentValidationText = editState?.value ?? baseValidationText;
            const rowEditState = editingEntries[editKey] || null;
            const isEditing = Boolean(rowEditState);
            const isSaving = Boolean(savingEntries[editKey]);
            const requiredLocked = isPinnedRequiredDocument(entry.entity_type, entry.document_type);
            const policyOptions = policyOptionsByEntity.get(entry.entity_type) || [];
            return (
              <tr key={`${entry.entity_type}:${entry.document_type}:${entry.requirement_id || index}`}>
                <td>{entry.entity_label || ENTITY_SECTION_LABELS[entry.entity_type] || entry.entity_type}</td>
                <td>
                  {isEditing ? (
                    <select
                      value={rowEditState.policy_canonical_type}
                      onChange={(event) => changeEditingEntry(entry, "policy_canonical_type", event.target.value)}
                    >
                      {policyOptions.map((option) => (
                        <option key={option.canonical_type} value={option.canonical_type}>
                          {option.name}
                        </option>
                      ))}
                      {!policyOptions.length ? <option value="">Sin política base</option> : null}
                    </select>
                  ) : (
                    entry.policy_name || "-"
                  )}
                </td>
                <td>
                  {isEditing ? (
                    <input
                      type="text"
                      value={rowEditState.label}
                      onChange={(event) => changeEditingEntry(entry, "label", event.target.value)}
                    />
                  ) : (
                    label
                  )}
                </td>
                <td>
                  {isEditing ? (
                    <input
                      type="text"
                      value={rowEditState.document_type}
                      onChange={(event) => changeEditingEntry(entry, "document_type", event.target.value)}
                    />
                  ) : (
                    entry.document_type
                  )}
                </td>
                <td>{entry.enabled ? "Sí" : "No"}</td>
                <td>
                  {requiredLocked ? (
                    <span>Fijo</span>
                  ) : isEditing ? (
                    <input
                      type="checkbox"
                      checked={Boolean(rowEditState.required)}
                      onChange={(event) => changeEditingEntry(entry, "required", event.target.checked)}
                    />
                  ) : entry.required ? "Sí" : "No"}
                </td>
                <td>
                  {isEditing ? (
                    <input
                      type="text"
                      value={rowEditState.allowed_statuses}
                      onChange={(event) => changeEditingEntry(entry, "allowed_statuses", event.target.value)}
                    />
                  ) : statuses.length ? statuses.join(" · ") : "Estados"}
                </td>
                <td>
                  {isEditing ? (
                    <input
                      type="number"
                      value={rowEditState.sort_order}
                      onChange={(event) => changeEditingEntry(entry, "sort_order", event.target.value)}
                    />
                  ) : (
                    entry.sort_order ?? "-"
                  )}
                </td>
                <td className="document-context-cell">
                  <div className="validation-context">
                    <button
                      type="button"
                      className="validation-context-toggle"
                      onClick={() => toggleValidationContext(entry)}
                      aria-expanded={isValidationExpanded}
                      aria-controls={contextId}
                    >
                      <span>Validaciones</span>
                      <span
                        className={`validation-context-toggle-icon ${isValidationExpanded ? "is-open" : ""}`}
                        aria-hidden="true"
                      >
                        ▼
                      </span>
                    </button>
                    <div
                      id={contextId}
                      className={`validation-context-copy ${isValidationExpanded ? "is-open" : ""}`}
                      aria-live="polite"
                    >
                      <textarea
                        rows={3}
                        value={currentValidationText}
                        onChange={(event) => handleValidationTextChange(entry, event.target.value)}
                        className="validation-context-textarea"
                      />
                    </div>
                  </div>
                </td>
                <td>
                  {isEditing ? (
                    <label>
                      <input
                        type="checkbox"
                        checked={Boolean(rowEditState.ai_relevant)}
                        onChange={(event) => changeEditingEntry(entry, "ai_relevant", event.target.checked)}
                      />
                      IA
                    </label>
                  ) : entry.ai_relevant ? "Sí" : "No (manual)"}
                </td>
                <td>
                  {isEditing ? (
                    <input
                      type="checkbox"
                      checked={Boolean(rowEditState.requires_signature)}
                      onChange={(event) => changeEditingEntry(entry, "requires_signature", event.target.checked)}
                    />
                  ) : entry.requires_signature ? "Sí" : "No"}
                </td>
                <td>
                  {isEditing ? (
                    <input
                      type="checkbox"
                      checked={Boolean(rowEditState.access_required)}
                      onChange={(event) => changeEditingEntry(entry, "access_required", event.target.checked)}
                    />
                  ) : entry.access_required ? "Sí" : "No"}
                </td>
                <td>
                  <div className="document-action-buttons">
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          className="submit-button tiny document-action-button"
                          onClick={() => saveEditingEntry(entry)}
                          disabled={isSaving}
                        >
                          {isSaving ? "Guardando..." : "Guardar"}
                        </button>
                        <button
                          type="button"
                          className="secondary tiny document-action-button"
                          onClick={() => cancelEditingEntry(entry)}
                          disabled={isSaving}
                        >
                          Cancelar
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="secondary tiny document-action-button"
                        onClick={() => startEditingEntry(entry)}
                      >
                        Editar
                      </button>
                    )}
                    {entry.enabled ? (
                      <button
                        type="button"
                        className="secondary tiny document-action-button document-action-button--disable"
                        onClick={() => handleDisable(entry)}
                        disabled={isSaving}
                      >
                        Deshabilitar
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="secondary tiny document-action-button document-action-button--disable"
                        onClick={() => handleEnable(entry)}
                        disabled={isSaving}
                      >
                        Habilitar
                      </button>
                    )}
                    <button
                      type="button"
                      className="secondary tiny document-action-button document-action-button--delete"
                      onClick={() => handleDelete(entry)}
                      disabled={isSaving}
                    >
                      Eliminar
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function getDocumentDisplayPriority(entry) {
  const entityType = String(entry?.entity_type || "");
  const documentType = String(entry?.document_type || "").toLowerCase();
  const pinnedPriority = PINNED_DOCUMENT_PRIORITIES[entityType]?.[documentType];
  if (pinnedPriority !== undefined) {
    return pinnedPriority;
  }

  return 100;
}

function isPinnedRequiredDocument(entityType, documentType) {
  const normalizedEntityType = String(entityType || "");
  const normalizedDocumentType = String(documentType || "").toLowerCase();
  return PINNED_DOCUMENT_PRIORITIES[normalizedEntityType]?.[normalizedDocumentType] !== undefined;
}

function getPolicyDisplayPriority(policy) {
  const entityType = String(policy?.entity_type || "");
  const components = Array.isArray(policy?.component_document_types) ? policy.component_document_types : [];
  const priorities = components
    .map((component) => getDocumentDisplayPriority({ entity_type: entityType, document_type: component.document_type }))
    .filter((value) => Number.isFinite(value));

  return priorities.length ? Math.min(...priorities) : 100;
}

function DocumentRequirementForm({ library = [], onSaved }) {
  const [formState, setFormState] = useState({
    entity_type: "driver",
    policy_canonical_type: "",
    document_type: "",
    document_label: "",
    required: true,
    enabled: true,
    requires_signature: false,
    access_required: true,
    allowed_statuses: "validated",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState("");

  const policiesForEntity = useMemo(
    () =>
      (library || [])
        .filter((item) => item.entity_type === formState.entity_type)
        .sort((a, b) => {
          const aPriority = getPolicyDisplayPriority(a);
          const bPriority = getPolicyDisplayPriority(b);
          if (aPriority !== bPriority) {
            return aPriority - bPriority;
          }
          return String(a.name || "").localeCompare(String(b.name || ""));
        }),
    [library, formState.entity_type]
  );

  useEffect(() => {
    if (!policiesForEntity.length) {
      if (formState.policy_canonical_type) {
        setFormState((prev) => ({ ...prev, policy_canonical_type: "" }));
      }
      return;
    }

    const currentPolicyExists = policiesForEntity.some((item) => item.canonical_type === formState.policy_canonical_type);
    if (!currentPolicyExists) {
      setFormState((prev) => ({
        ...prev,
        policy_canonical_type: policiesForEntity[0].canonical_type,
      }));
    }
  }, [policiesForEntity, formState.policy_canonical_type]);

  const selectedPolicy = useMemo(
    () => policiesForEntity.find((item) => item.canonical_type === formState.policy_canonical_type) || null,
    [policiesForEntity, formState.policy_canonical_type]
  );

  const documentsForEntity = useMemo(() => {
    if (!selectedPolicy) {
      return [];
    }
    return (selectedPolicy.component_document_types || []).map((component) => ({
      ...component,
      policy_name: selectedPolicy.name,
      policy_canonical_type: selectedPolicy.canonical_type,
      description: component.description || selectedPolicy.description || "",
      ai_relevant: component.ai_relevant ?? selectedPolicy.ai_relevant ?? true,
      required_statuses: component.required_statuses || selectedPolicy.required_statuses || ["validated"],
    })).sort((a, b) => {
      const aPriority = getDocumentDisplayPriority({ entity_type: formState.entity_type, document_type: a.document_type });
      const bPriority = getDocumentDisplayPriority({ entity_type: formState.entity_type, document_type: b.document_type });
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }
      return String(a.label || a.document_type || "").localeCompare(String(b.label || b.document_type || ""));
    });
  }, [selectedPolicy, formState.entity_type]);

  const selectedDefinition = useMemo(
    () => documentsForEntity.find((item) => item.document_type === formState.document_type),
    [documentsForEntity, formState.document_type]
  );
  const requiredLocked = isPinnedRequiredDocument(formState.entity_type, formState.document_type);

  useEffect(() => {
    if (!documentsForEntity.length) {
      if (formState.document_type) {
        setFormState((prev) => ({ ...prev, document_type: "" }));
      }
      return;
    }

    if (!String(formState.document_type || "").trim()) {
      setFormState((prev) => ({ ...prev, document_type: documentsForEntity[0].document_type }));
    }
  }, [documentsForEntity, formState.document_type]);

  useEffect(() => {
    if (!requiredLocked || formState.required) {
      return;
    }
    setFormState((prev) => ({ ...prev, required: true }));
  }, [requiredLocked, formState.required]);

  const policyName = selectedDefinition?.policy_name || "Política base";
  const documentLabel = formState.document_label || selectedDefinition?.label || selectedDefinition?.document_type || formState.document_type;
  const aiLabel = selectedDefinition?.ai_relevant ? "IA" : "Manual";

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formState.document_type.trim()) {
      setFeedback("Seleccioná un documento válido para crear el requisito.");
      return;
    }
    setSaving(true);
    setFeedback("");
    try {
      await postJson("/document-policies/catalog/upsert", {
        entity_type: formState.entity_type,
        policy_canonical_type: formState.policy_canonical_type || null,
        document_type: formState.document_type.trim(),
        label: documentLabel,
        enabled: formState.enabled,
        required: requiredLocked ? true : formState.required,
        requires_signature: formState.requires_signature,
        access_required: formState.access_required,
        allowed_statuses: formState.allowed_statuses ? [formState.allowed_statuses] : [],
        notes: formState.notes || null,
      });
      if (onSaved) {
        onSaved();
      }
      setFormState((current) => ({
        ...current,
        policy_canonical_type: current.policy_canonical_type,
        document_type: "",
        document_label: "",
        allowed_statuses: "validated",
        notes: "",
      }));
      setFeedback("Documento agregado correctamente.");
    } catch (error) {
      setFeedback(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="admin-form" onSubmit={handleSubmit}>
      <h3>Agregar documento al catálogo</h3>
      <div className="admin-form-grid">
        <label className="field">
          <span>Entidad</span>
          <select
            value={formState.entity_type}
            onChange={(event) => setFormState((prev) => ({ ...prev, entity_type: event.target.value }))}
          >
            {ENTITY_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Tipo de documento</span>
          <input
            list={`document-types-${formState.entity_type}`}
            value={formState.document_type}
            onChange={(event) => setFormState((prev) => ({ ...prev, document_type: event.target.value }))}
            placeholder="Ej.: seguro_ambiental"
          />
          <datalist id={`document-types-${formState.entity_type}`}>
            {documentsForEntity.map((item) => (
              <option key={item.document_type} value={item.document_type}>
                {item.label || item.document_type}
              </option>
            ))}
          </datalist>
          <small className="admin-panel-copy">
            Podés elegir uno existente o escribir uno nuevo.
          </small>
          {selectedDefinition?.description ? (
            <small className="admin-panel-copy">{selectedDefinition.description}</small>
          ) : null}
        </label>
        <label className="field">
          <span>Política</span>
          <select
            value={formState.policy_canonical_type}
            onChange={(event) => setFormState((prev) => ({ ...prev, policy_canonical_type: event.target.value }))}
          >
            {policiesForEntity.map((policy) => (
              <option key={policy.canonical_type} value={policy.canonical_type}>
                {policy.name}
              </option>
            ))}
            {!policiesForEntity.length ? <option value="">Sin política base</option> : null}
          </select>
          <small className="admin-panel-copy">{policyName}</small>
        </label>
        <label className="field">
          <span>Documento</span>
          <input
            type="text"
            value={documentLabel}
            onChange={(event) => setFormState((prev) => ({ ...prev, document_label: event.target.value }))}
          />
        </label>
        <label className="field">
          <span>Estados permitidos</span>
          <select
            value={formState.allowed_statuses}
            onChange={(event) => setFormState((prev) => ({ ...prev, allowed_statuses: event.target.value }))}
          >
            {(VALIDATION_STATUS_OPTIONS || []).map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <small className="admin-panel-copy">{aiLabel} · {selectedDefinition?.description || ""}</small>
        </label>
        <label className="field">
          <span>Validaciones</span>
          <textarea
            rows="3"
            value={formState.notes}
            onChange={(event) => setFormState((prev) => ({ ...prev, notes: event.target.value }))}
          />
        </label>
        <div className="admin-form-checkboxes">
          <label>
            <input
              type="checkbox"
              checked={formState.enabled}
              onChange={(event) => setFormState((prev) => ({ ...prev, enabled: event.target.checked }))}
            />
            Activo
          </label>
          {requiredLocked ? <span className="admin-panel-copy">Obligatorio fijo para este documento.</span> : (
            <label>
              <input
                type="checkbox"
                checked={formState.required}
                onChange={(event) => setFormState((prev) => ({ ...prev, required: event.target.checked }))}
              />
              Obligatorio
            </label>
          )}
          <label>
            <input
              type="checkbox"
              checked={formState.requires_signature}
              onChange={(event) => setFormState((prev) => ({ ...prev, requires_signature: event.target.checked }))}
            />
            Requiere firma
          </label>
          <label>
            <input
              type="checkbox"
              checked={formState.access_required}
              onChange={(event) => setFormState((prev) => ({ ...prev, access_required: event.target.checked }))}
            />
            Acceso requerido
          </label>
        </div>
      </div>
      <div className="admin-form-actions">
        <button className="submit-button" type="submit" disabled={saving}>
          {saving ? "Guardando..." : "Agregar documento"}
        </button>
        {feedback ? <span className="status-message">{feedback}</span> : null}
      </div>
    </form>
  );
}

const DOCUMENT_ENTITY_OPTIONS = [
  { value: "driver", label: "Chofer" },
  { value: "transport_company", label: "Empresa de transporte" },
  { value: "tractor_unit", label: "Unidad tractora" },
  { value: "trailer_unit", label: "Unidad remolque" },
];

function DocumentCatalogViewer({ groupedLibrary, entityType, onSelectEntity }) {
  const entries = (groupedLibrary[entityType] || [])
    .flatMap((entry) =>
      (entry.component_document_types || []).map((component) => ({
        ...component,
        entity_type: entry.entity_type,
        policyName: entry.name,
        canonicalType: entry.canonical_type,
      }))
    )
    .sort((a, b) => {
      const aPriority = getDocumentDisplayPriority(a);
      const bPriority = getDocumentDisplayPriority(b);
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }
      const aOrder = Number.isFinite(Number(a.sort_order)) ? Number(a.sort_order) : 100;
      const bOrder = Number.isFinite(Number(b.sort_order)) ? Number(b.sort_order) : 100;
      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }
      return String(a.label || a.document_type || "").localeCompare(String(b.label || b.document_type || ""));
    });

  return (
    <section className="document-catalog-viewer">
      <div className="document-catalog-header">
        <label className="field">
          <span>Entidad</span>
          <select value={entityType} onChange={(event) => onSelectEntity(event.target.value)}>
            {DOCUMENT_ENTITY_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {entries.length ? (
        <table className="document-catalog-table">
          <thead>
            <tr>
              <th>Política</th>
              <th>Documento</th>
              <th>Etiqueta</th>
              <th>Descripción</th>
              <th>Estados</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr key={`${entry.canonicalType || ""}:${entry.document_type}`}>
                <td>{entry.policyName}</td>
                <td>{entry.document_type}</td>
                <td>{entry.label}</td>
                <td>{entry.description || "-"}</td>
                <td>{(entry.required_statuses || []).join(" · ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="status-message">No hay documentos definidos para esta entidad.</p>
      )}
    </section>
  );
}

function buildInitialForm(definition) {
  if (!definition) {
    return EMPTY_FORM;
  }

  return definition.fields.reduce((accumulator, field) => {
    accumulator[field.key] = "";
    return accumulator;
  }, {});
}

function normalizeFieldValue(value, type) {
  if (value === null || value === undefined) {
    return type === "boolean" ? "false" : "";
  }

  if (type === "date" && typeof value === "string") {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : "";
  }

  if (type === "boolean") {
    return value ? "true" : "false";
  }

  return String(value);
}

function displayRecordValue(value) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return translateAdminUiValue(value);
}

function humanizeKey(value) {
  const normalized = String(value || "").trim().toLowerCase();
  const map = {
    transport_users: "Usuarios transporte",
    transport_companies: "Empresas de transporte",
    consumer_companies: "Empresas consumidoras",
    consumer_users: "Usuarios",
    authorization_requests: "Solicitudes de habilitación",
    document_access_requests: "Solicitudes de acceso documental",
    access_grants: "Accesos documentales",
    auth_codes: "Códigos",
    email_logs: "Emails",
    operations: "Operaciones",
    documents: "Documentos",
    drivers: "Choferes",
    trailers: "Remolques",
    vehicles: "Vehículos",
    operation_requirements: "Requisitos operativos",
    current_status: "Estado actual",
    previous_status: "Estado anterior",
    new_status: "Estado nuevo",
    related_operation_id: "Operación relacionada",
    entity_type: "Tipo de entidad",
    entity_id: "ID de entidad",
    email_verified: "Email validado",
    phone_verified: "WhatsApp validado",
    phone_number: "WhatsApp",
    company_cuit: "CUIT principal",
    profile_name: "Nombre de perfil",
    last_login_at: "Último login",
    created_at: "Creado",
    updated_at: "Actualizado",
  };

  if (map[normalized]) {
    return map[normalized];
  }

  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function translateAdminUiValue(value) {
  const normalized = String(value || "").trim().toLowerCase();
  const map = {
    active: "Activo",
    inactive: "Inactivo",
    disabled: "Deshabilitado",
    pending: "Pendiente",
    pending_email_verification: "Pendiente de verificación",
    pending_validation: "Pendiente de validación",
    pending_signature: "Pendiente de firma",
    pending_acceptance: "Pendiente de aceptación",
    approved: "Aprobado",
    rejected: "Rechazado",
    observed: "Observado",
    validated: "Validado",
    access_denied: "Acceso denegado",
    access_granted: "Acceso aprobado",
    revoked: "Revocado",
    denied: "Denegado",
    compliant: "Conforme",
    compliant_with_observations: "Conforme con observaciones",
    non_compliant: "No conforme",
    local: "Email",
    email: "Email",
    email_code: "Código por email",
    whatsapp_code: "Código por WhatsApp",
    driver: "Chofer",
    transport_company: "Empresa de transporte",
    consumer_company: "Empresa consumidora",
    tractor_unit: "Unidad tractora",
    trailer_unit: "Unidad remolque",
  };

  if (map[normalized]) {
    return map[normalized];
  }

  return String(value);
}

function renderSummaryValue(detail, key, value, onNavigate) {
  const target = resolveSummaryNavigationTarget(detail, key, value);
  if (!target || typeof onNavigate !== "function") {
    return displayRecordValue(value);
  }

  return (
    <button className="admin-inline-link" type="button" onClick={() => onNavigate(target)}>
      {displayRecordValue(value)}
    </button>
  );
}

function renderRelatedPrimary(groupKey, item, onNavigate) {
  const target = resolveRelatedNavigationTarget(groupKey, item);
  const label = getRelatedItemPrimary(groupKey, item);
  if (!target || typeof onNavigate !== "function") {
    return label;
  }

  return (
    <button className="admin-inline-link" type="button" onClick={() => onNavigate(target)}>
      {label}
    </button>
  );
}

function resolveSummaryNavigationTarget(detail, key, value) {
  if (!detail || value === null || value === undefined || value === "") {
    return null;
  }

  if (detail.entity_type === "operations") {
    if (key === "driver_id") {
      return { registryType: "choferes", identifier: value, moduleSearchValue: String(value) };
    }
    if (key === "transport_company_id") {
      return { registryType: "titulares", identifier: value, moduleSearchValue: String(value) };
    }
    if (key === "consumer_company_id") {
      return { registryType: "consumer_companies", identifier: value, moduleSearchValue: String(value) };
    }
  }

  if (detail.entity_type === "document_access_grants" && key === "document_id") {
    return { registryType: "documentos", identifier: value, moduleSearchValue: String(value) };
  }

  return null;
}

function resolveRelatedNavigationTarget(groupKey, item) {
  if (!item) {
    return null;
  }

  if (groupKey === "operations" && item.id) {
    return { registryType: "operations", identifier: item.id, moduleSearchValue: String(item.id) };
  }
  if (groupKey === "users" && item.id) {
    return { registryType: "consumer_users", identifier: item.id, moduleSearchValue: String(item.email || item.id) };
  }
  if (groupKey === "drivers" && item.cuit) {
    return { registryType: "choferes", identifier: item.cuit, moduleSearchValue: String(item.cuit) };
  }
  if (groupKey === "transport_users" && item.id) {
    return { registryType: "transport_users", identifier: item.id, moduleSearchValue: String(item.email || item.id) };
  }
  if (groupKey === "transport_companies" && item.cuit) {
    return { registryType: "titulares", identifier: item.cuit, moduleSearchValue: String(item.cuit) };
  }
  if ((groupKey === "documents" || groupKey === "access_grants") && item.id) {
    return { registryType: "documentos", identifier: item.id, moduleSearchValue: String(item.id) };
  }
  if (groupKey === "authorization_requests" && item.id) {
    return {
      registryType: "consumer_entity_authorizations",
      identifier: item.id,
      moduleSearchValue: String(item.entity_label || item.id),
    };
  }

  return null;
}

function resolveOperationRequirementEntityTarget(item) {
  if (!item?.entity_type || !item?.entity_id) {
    return null;
  }

  if (item.entity_type === "driver") {
    return { registryType: "choferes", identifier: item.entity_id, moduleSearchValue: String(item.entity_id) };
  }
  if (item.entity_type === "transport_company") {
    return { registryType: "titulares", identifier: item.entity_id, moduleSearchValue: String(item.entity_id) };
  }
  if (item.entity_type === "consumer_company") {
    return { registryType: "consumer_companies", identifier: item.entity_id, moduleSearchValue: String(item.entity_id) };
  }

  return null;
}

function getRelatedItemPrimary(groupKey, item) {
  if (groupKey === "operation_requirements") {
    return displayRecordValue(
      [
        item.entity_type,
        item.entity_id,
        item.document_type,
      ]
        .filter(Boolean)
        .join(" · ")
    );
  }

  if (groupKey === "auth_codes") {
    return displayRecordValue(item.code || item.token_type || item.id);
  }

  if (groupKey === "email_logs") {
    return displayRecordValue(item.email || item.recipient || item.provider_message_id || item.id);
  }

  if (groupKey === "operations") {
    return formatOperationReference(item.id);
  }

  return displayRecordValue(item.dominio || item.cuit || item.email || item.id);
}

function getRelatedItemSecondary(groupKey, item) {
  if (groupKey === "operation_requirements") {
    return displayRecordValue(
      [
        item.result,
        item.operational_status,
        item.document_status,
        item.nombre_archivo || (item.document_id ? `Documento #${item.document_id}` : "Sin documento consultado"),
      ]
        .filter(Boolean)
        .join(" · ")
    );
  }

  if (groupKey === "auth_codes") {
    return displayRecordValue(
      [
        item.purpose || item.token_type,
        item.channel,
        item.used_at ? "Usado" : "Pendiente",
      ]
        .filter(Boolean)
        .join(" · ")
    );
  }

  if (groupKey === "email_logs") {
    return displayRecordValue(
      [
        item.email_type,
        item.status,
        item.last_event_type,
      ]
        .filter(Boolean)
        .join(" · ")
    );
  }

  if (groupKey === "operations") {
    return displayRecordValue(
      [
        item.compliance_status || item.status,
        item.created_at ? formatDateTime(item.created_at) : null,
      ]
        .filter(Boolean)
        .join(" · ")
    );
  }

  return displayRecordValue(item.nombre || item.razon_social || item.marca || item.status || item.tipo_documento);
}

function getRelatedItemMeta(groupKey, item) {
  if (groupKey === "operation_requirements") {
    const componentSummary = Array.isArray(item.component_documents_json)
      ? item.component_documents_json
          .map((component) =>
            [component.label || component.document_type, component.operational_status]
              .filter(Boolean)
              .join(": ")
          )
          .filter(Boolean)
          .join(" | ")
      : "";
    const statuses = Array.isArray(item.allowed_statuses_json)
      ? item.allowed_statuses_json.join(", ")
      : displayRecordValue(item.allowed_statuses_json);
    return displayRecordValue(
      [
        item.required ? "Obligatorio" : "Opcional",
        item.access_required ? "Requiere acceso" : null,
        item.requires_signature ? "Requiere firma" : null,
        statuses ? `Permitidos: ${statuses}` : null,
        componentSummary ? `Componentes: ${componentSummary}` : null,
        item.validation_reason || null,
      ]
        .filter(Boolean)
        .join(" · ")
    );
  }

  if (groupKey === "auth_codes") {
    return displayRecordValue(
      [
        item.expires_at ? `Expira ${formatDateTime(item.expires_at)}` : null,
        item.created_at ? `Creado ${formatDateTime(item.created_at)}` : null,
      ]
        .filter(Boolean)
        .join(" · ")
    );
  }

  if (groupKey === "email_logs") {
    return displayRecordValue(
      [
        item.provider ? item.provider.toUpperCase() : null,
        item.last_event_at ? formatDateTime(item.last_event_at) : formatDateTime(item.created_at || item.updated_at),
      ]
        .filter(Boolean)
        .join(" · ")
    );
  }

  if (groupKey === "operations") {
    return displayRecordValue(item.id ? `Interno #${item.id}` : "");
  }

  return displayRecordValue(formatDateTime(item.created_at || item.updated_at));
}

function formatOperationReference(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return displayRecordValue(value);
  }
  return `OP-${numericValue.toString(36).toUpperCase().padStart(4, "0")}`;
}
